<template>
  <toolbar-container>
    <template #toolbar>
      <main-toolbar>
        <v-text-field
          :value="accountNameFilter"
          autofocus
          style="max-width: 350px"
          class="pt-0 mt-2 mr-5"
          dense
          clearable
          :label="$t('label.restaurant_name')"
          hide-details
          @input="accountNameFilter = $event ?? ''"
          @click:clear="accountNameFilter = ''"
        />
        <v-select
          v-model="selectedDays"
          :items="rangeItems"
          dense
          hide-details
          class="mt-2"
          style="max-width: 180px; min-width: 140px"
          :label="$t('label.date_range')"
        />
        <v-spacer />
      </main-toolbar>
    </template>

    <paywall-upgrade-alert
      v-if="!currentAccountHasMultidash"
      class="ma-4"
      :text="$t('message.multidash_upgrade_required_body')"
      :plans="['business', 'ultimate']"
    />
    <p
      v-if="totalsExcludeLocked"
      class="text-caption text--secondary px-4 py-2 mb-0 d-flex justify-center"
    >
      <v-icon
        small
        color="warning"
        class="mr-1 mt-0 flex-shrink-0"
      >
        mdi-alert-outline
      </v-icon>
      {{ $t('message.multidash_totals_exclude_locked') }}
    </p>

    <v-divider v-if="!currentAccountHasMultidash || totalsExcludeLocked" />

    <v-row
      class="ma-0 py-0"
      :class="{ 'multidash-summary--five': !groupHasFeedback }"
      :justify="groupHasFeedback ? 'start' : 'center'"
    >
      <v-col
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.guests')"
          :count="totalText($n(totals.guests))"
          :trend="totalTrend(totals.guestsTrend)"
          :trend-percent="totalPercent(totals.guestsPercent)"
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.reservations')"
          :count="totalText($n(totals.reservations))"
          :trend="totalTrend(totals.reservationsTrend)"
          :trend-percent="totalPercent(totals.reservationsPercent)"
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.revenue')"
          :count="totalText(totalsRevenueHead.text)"
          :trend="totalTrend(totalsRevenueHead.trend)"
          :trend-percent="totalPercent(totalsRevenueHead.percent)"
          :extra-counts="totalsShown ? totalsRevenueExtra : []"
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.no_shows')"
          :count="totalText($n(totals.noShows))"
          :trend="totalTrend(totals.noShowsTrend)"
          :trend-percent="totalPercent(totals.noShowsPercent)"
          inverse-trend
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.cancellations')"
          :count="totalText($n(totals.cancellations))"
          :trend="totalTrend(totals.cancellationsTrend)"
          :trend-percent="totalPercent(totals.cancellationsPercent)"
          inverse-trend
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
      <v-col
        v-if="groupHasFeedback"
        class="pa-0"
        cols="6"
        sm="4"
        md="2"
      >
        <numeric-list-card
          :title="$t('label.rating')"
          :count="totalText(formatRating(totals.rating))"
          :trend="totalTrend(totals.ratingTrend)"
          :trend-percent="totalPercent(totals.ratingPercent)"
          :list-titles="[]"
          :list-values="[]"
        />
      </v-col>
    </v-row>

    <div
      class="multidash__root noselect"
      :class="{ 'multidash__root--no-feedback': !groupHasFeedback }"
    >
      <div class="grid-table-wrapper">
        <div class="grid-row header-row">
          <div class="grid-cell col-B" />
          <div class="grid-cell col-C justify-start">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">
              {{ $t('label.restaurant_name') }}
            </span>
          </div>
          <div class="grid-cell col-D justify-start d-none d-lg-flex">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">
              {{ $t('label.city') }}
            </span>
          </div>
          <div class="grid-cell col-E">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.guests') }}</span>
          </div>
          <div class="grid-cell col-F">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.reservations') }}</span>
          </div>
          <div class="grid-cell col-J">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.revenue') }}</span>
          </div>
          <div class="grid-cell col-G">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.no_shows') }}</span>
          </div>
          <div class="grid-cell col-H">
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.cancellations') }}</span>
          </div>
          <div
            v-if="groupHasFeedback"
            class="grid-cell col-I"
          >
            <span class="text-subtitle-2 font-weight-bold text--secondary ellipsize">{{ $t('label.rating') }}</span>
          </div>
        </div>
        <div
          v-for="row in accountRows"
          :key="row.account.id"
          :data-account-id="row.account.id"
          class="grid-row account-row"
          @click="changeAccount(row.account)"
        >
          <div class="multidash__avatar grid-cell justify-center col-B">
            <div class="d-flex align-center">
              <v-icon
                v-if="!accountNameFilter"
                class="pr-2 pr-md-4 reorder-handle"
                small
              >
                mdi-arrow-up-down
              </v-icon>
              <account-avatar
                :account-name="row.account.accountName"
                :color="row.account.color"
                disable-tooltip
              />
            </div>
          </div>

          <div class="multidash__account-name grid-cell col-C">
            <div class="multidash__account-name-text">
              <span class="d-block font-weight-medium text-left">{{ row.account.accountName }}</span>
              <span class="d-block d-lg-none text-body-2 text--secondary text-left">{{ row.stats.city }}</span>
            </div>
            <v-spacer v-if="$vuetify.breakpoint.xs" />
            <div class="mx-2">
              <v-chip
                v-if="currentAccountId === row.account.id"
                x-small
                class="white--text"
                label
                color="brand"
              >
                {{ $t('label.logged_in') }}
              </v-chip>
            </div>
          </div>

          <div class="multidash__city grid-cell col-D justify-start d-none d-lg-flex">
            <span class="text--secondary">{{ row.stats.city }}</span>
          </div>

          <div class="multidash__metric grid-cell flex-column text-center overflow-hidden col-E">
            <span class="metric-label">{{ $t('label.guests') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`guests-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed)"
              :line-width="3"
              :value="row.stats.guestsSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.guestsTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.guestsTrend)"
              >
                {{ trendIcon(row.stats.guestsTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : $n(row.stats.guests) }}
            </p>
          </div>
          <div class="multidash__metric grid-cell flex-column text-center overflow-hidden col-F">
            <span class="metric-label">{{ $t('label.reservations') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`reservations-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed)"
              :line-width="3"
              :value="row.stats.reservationsSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.reservationsTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.reservationsTrend)"
              >
                {{ trendIcon(row.stats.reservationsTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : $n(row.stats.reservations) }}
            </p>
          </div>
          <div class="multidash__metric grid-cell flex-column text-center overflow-hidden col-J">
            <span class="metric-label">{{ $t('label.revenue') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`revenue-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed)"
              :line-width="3"
              :value="row.stats.revenueSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.revenueTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.revenueTrend)"
              >
                {{ trendIcon(row.stats.revenueTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : formatRevenue(row.stats.revenue, row.stats.currencyCode) }}
            </p>
          </div>
          <div class="multidash__metric grid-cell flex-column text-center overflow-hidden col-G">
            <span class="metric-label">{{ $t('label.no_shows') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`noshows-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed, true)"
              :line-width="3"
              :value="row.stats.noShowsSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.noShowRateTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.noShowRateTrend, true)"
              >
                {{ trendIcon(row.stats.noShowRateTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : formatPercent(row.stats.noShowRate) }}
            </p>
          </div>
          <div class="multidash__metric grid-cell flex-column text-center overflow-hidden col-H">
            <span class="metric-label">{{ $t('label.cancellations') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`cancellations-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed, true)"
              :line-width="3"
              :value="row.stats.cancellationsSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.cancellationRateTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.cancellationRateTrend, true)"
              >
                {{ trendIcon(row.stats.cancellationRateTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : formatPercent(row.stats.cancellationRate) }}
            </p>
          </div>
          <div
            v-if="groupHasFeedback"
            class="multidash__metric grid-cell flex-column text-center overflow-hidden col-I"
          >
            <span class="metric-label">{{ $t('label.rating') }}</span>
            <v-icon
              v-if="row.stats.locked"
              small
              color="grey lighten-1"
              class="my-3"
              :title="$t('message.multidash_upgrade_required_body')"
            >
              mdi-lock-outline
            </v-icon>
            <v-sparkline
              v-else
              :key="`rating-${row.account.id}-${selectedDays}`"
              class="multidash__sparkline"
              :smooth="16"
              :gradient="gradientFor(row.stats.dimmed || !row.stats.enableFeedback)"
              :line-width="3"
              :value="row.stats.ratingSeries"
              stroke-linecap="round"
            />
            <p
              v-if="!row.stats.locked"
              class="text-body-2 mb-1 font-weight-medium d-flex align-center justify-center multidash__metric-value"
              :class="{ 'text--disabled': row.stats.dimmed || !row.stats.enableFeedback }"
            >
              <v-icon
                v-if="!row.stats.dimmed && row.stats.ratingTrend !== 'flat'"
                x-small
                :color="trendColor(row.stats.ratingTrend)"
              >
                {{ trendIcon(row.stats.ratingTrend) }}
              </v-icon>
              {{ row.stats.dimmed ? '—' : formatRating(row.stats.rating) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </toolbar-container>
</template>

<script setup lang="ts">
import store from '@/store/store';
import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';
import MainToolbar from '@/components/views/main/MainToolbar.vue';
import { useRouter } from 'vue-router/composables';
import UserAccount from '@/model/UserAccount';
import Sortable from 'sortablejs';
import AccountAvatar from '@/components/views/main/AccountAvatar.vue';
import NumericListCard from '@/components/views/dashboard/NumericListCard.vue';
import PaywallUpgradeAlert from '@/components/dialogs/PaywallUpgradeAlert.vue';
import useAccountSwitcher, { hasMultipleAccounts } from '@/composables/account-switcher';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import i18n from '@/plugins/i18n';
import grpcClient from '@/grpc-api/grpc-client';
import { performAction, performSaveAction } from '@/services/vue-utils';
import { DashboardAccountRevenue, DashboardMultiAccountStatsResponse } from '@/grpc-gen/dashboard_service_pb';
import {
  accumulateCurrencySeries, buildDayIndexAxis, CurrencySeries, DailySeries,
  emptyDailySeries, fillDailySeries, fillRevenueSeries, pickDominantCurrency,
} from '@/services/multidash-utils';

const RANGE_OPTIONS = [7, 14, 30] as const;
const HISTORY_DAYS = 60;
const REFRESH_MS = 5 * 60 * 1000;

type RangeDays = typeof RANGE_OPTIONS[number];
type Trend = 'up' | 'down' | 'flat';

interface AccountData {
  city: string;
  enableFeedback: boolean;
  allowMultidash: boolean;
  currencyCode: string;
  series: DailySeries;
  revenues: DashboardAccountRevenue[];
  revenueSeries: number[];
}

interface AccountStats {
  city: string;
  enableFeedback: boolean;
  locked: boolean;
  dimmed: boolean;
  currencyCode: string;
  ratingSum: number;
  ratingCount: number;
  prevRatingSum: number;
  prevRatingCount: number;
  guests: number;
  prevGuests: number;
  guestsSeries: number[];
  guestsTrend: Trend;
  reservations: number;
  prevReservations: number;
  reservationsSeries: number[];
  reservationsTrend: Trend;
  revenue: number;
  prevRevenue: number;
  revenueSeries: number[];
  revenueTrend: Trend;
  noShows: number;
  prevNoShows: number;
  noShowsSeries: number[];
  noShowsTrend: Trend;
  noShowRate: number;
  noShowRateTrend: Trend;
  cancellations: number;
  prevCancellations: number;
  cancellationsSeries: number[];
  cancellationsTrend: Trend;
  cancellationRate: number;
  cancellationRateTrend: Trend;
  rating: number;
  prevRating: number;
  ratingSeries: number[];
  ratingTrend: Trend;
}

const router = useRouter();
const { switchAccount, currentAccountId } = useAccountSwitcher(router);

const accountNameFilter = ref('');
const selectedDays = ref<RangeDays>(30);
const rangeItems = RANGE_OPTIONS.map((d) => ({
  value: d,
  text: i18n.tc('label.x_days', d),
}));

const statsResponse = ref<DashboardMultiAccountStatsResponse | null>(null);

const dayAxis = computed(() => (statsResponse.value
  ? buildDayIndexAxis(statsResponse.value.beginDate, statsResponse.value.endDate)
  : []));

const accountData = computed(() => {
  const map = new Map<number, AccountData>();
  const resp = statsResponse.value;
  if (!resp) return map;
  resp.accounts.forEach((a) => {
    const currencyCode = pickDominantCurrency(a.revenues);
    map.set(a.accountId, {
      city: a.city,
      enableFeedback: a.enableFeedback,
      allowMultidash: a.allowMultidash,
      currencyCode,
      series: fillDailySeries(a.days, dayAxis.value),
      revenues: a.revenues,
      // the row cell shows the account's dominant currency; stray other-currency
      // payments still reach the per-currency totals via accumulateCurrencySeries
      revenueSeries: fillRevenueSeries(a.revenues, dayAxis.value, currencyCode),
    });
  });
  return map;
});

// hide the whole rating column when no account in the group has feedback
const groupHasFeedback = computed(() => {
  const resp = statsResponse.value;
  if (!resp) return true;
  return resp.accounts.some((a) => a.enableFeedback);
});

// paywall is for the logged-in restaurant, even if others in the group are unlocked
const currentAccountHasMultidash = computed(() => {
  const resp = statsResponse.value;
  if (!resp) return true;
  return accountData.value.get(currentAccountId.value)?.allowMultidash ?? true;
});

// no account on the plan -> the metrics stay as a greyed-out teaser
const groupHasMultidash = computed(() => {
  const resp = statsResponse.value;
  if (!resp) return true;
  return resp.accounts.some((a) => a.allowMultidash);
});

// mixed access: totals only cover unlocked restaurants; skip when the
// paywall is already explaining why this account is locked
const totalsExcludeLocked = computed(() => {
  const resp = statsResponse.value;
  if (!resp || !currentAccountHasMultidash.value || !groupHasMultidash.value) return false;
  return resp.accounts.some((a) => !a.allowMultidash);
});

function sum(values: number[]) {
  return values.reduce((total, value) => total + value, 0);
}

function trendOf(current: number, previous: number): Trend {
  if (current > previous) return 'up';
  if (current < previous) return 'down';
  return 'flat';
}

function formatDeltaPercent(current: number, previous: number) {
  if (!previous) return '';
  const rounded = Math.round(((current - previous) / Math.abs(previous)) * 100);
  if (!rounded) return '';
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

function slicePeriod(values: number[], days: number, periodOffset: number): number[] {
  const end = values.length - periodOffset * days;
  return values.slice(end - days, end);
}

function toRate(part: number, total: number) {
  return total ? (part / total) * 100 : 0;
}

function getAccountStats(id: number): AccountStats {
  const days = selectedDays.value;
  const data = accountData.value.get(id);
  const series = data?.series ?? emptyDailySeries(dayAxis.value.length || HISTORY_DAYS);
  const enableFeedback = data?.enableFeedback ?? true;
  // locked = known to be off-plan (drives the lock icon); dimmed = nothing to
  // show, which also covers stats not loaded yet or an account missing from
  // the response - those must not render as real (zero) values
  const locked = data ? !data.allowMultidash : false;
  const dimmed = !data || locked;
  const currencyCode = data?.currencyCode ?? '';

  const guestsSeries = slicePeriod(series.guestsSeries, days, 0);
  const prevGuestsSeries = slicePeriod(series.guestsSeries, days, 1);
  const reservationsSeries = slicePeriod(series.reservationsSeries, days, 0);
  const prevReservationsSeries = slicePeriod(series.reservationsSeries, days, 1);
  const revenueFull = data?.revenueSeries ?? new Array<number>(series.guestsSeries.length).fill(0);
  const revenueSeries = slicePeriod(revenueFull, days, 0);
  const prevRevenueSeries = slicePeriod(revenueFull, days, 1);
  const noShowsSeries = slicePeriod(series.noShowsSeries, days, 0);
  const prevNoShowsSeries = slicePeriod(series.noShowsSeries, days, 1);
  const cancellationsSeries = slicePeriod(series.cancellationsSeries, days, 0);
  const prevCancellationsSeries = slicePeriod(series.cancellationsSeries, days, 1);
  const ratingSumSeries = slicePeriod(series.ratingSumSeries, days, 0);
  const prevRatingSumSeries = slicePeriod(series.ratingSumSeries, days, 1);
  const ratingCountSeries = slicePeriod(series.ratingCountSeries, days, 0);
  const prevRatingCountSeries = slicePeriod(series.ratingCountSeries, days, 1);
  const ratingSeries = enableFeedback
    ? ratingSumSeries.map((s, i) => (ratingCountSeries[i] ? s / ratingCountSeries[i] : 0))
    : new Array<number>(ratingSumSeries.length).fill(0);

  const guests = sum(guestsSeries);
  const prevGuests = sum(prevGuestsSeries);
  const reservations = sum(reservationsSeries);
  const prevReservations = sum(prevReservationsSeries);
  const revenue = sum(revenueSeries);
  const prevRevenue = sum(prevRevenueSeries);
  const noShows = sum(noShowsSeries);
  const prevNoShows = sum(prevNoShowsSeries);
  const cancellations = sum(cancellationsSeries);
  const prevCancellations = sum(prevCancellationsSeries);
  const ratingSum = sum(ratingSumSeries);
  const ratingCount = sum(ratingCountSeries);
  const prevRatingSum = sum(prevRatingSumSeries);
  const prevRatingCount = sum(prevRatingCountSeries);
  const rating = ratingCount ? ratingSum / ratingCount : 0;
  const prevRating = prevRatingCount ? prevRatingSum / prevRatingCount : 0;
  const noShowRate = toRate(noShows, reservations);
  const prevNoShowRate = toRate(prevNoShows, prevReservations);
  const cancellationRate = toRate(cancellations, reservations);
  const prevCancellationRate = toRate(prevCancellations, prevReservations);

  const stats: AccountStats = {
    city: data?.city ?? '',
    enableFeedback,
    locked,
    dimmed,
    currencyCode,
    ratingSum,
    ratingCount,
    prevRatingSum,
    prevRatingCount,
    guests,
    prevGuests,
    guestsSeries,
    guestsTrend: trendOf(guests, prevGuests),
    reservations,
    prevReservations,
    reservationsSeries,
    reservationsTrend: trendOf(reservations, prevReservations),
    revenue,
    prevRevenue,
    revenueSeries,
    revenueTrend: trendOf(revenue, prevRevenue),
    noShows,
    prevNoShows,
    noShowsSeries,
    noShowsTrend: trendOf(noShows, prevNoShows),
    noShowRate,
    noShowRateTrend: trendOf(noShowRate, prevNoShowRate),
    cancellations,
    prevCancellations,
    cancellationsSeries,
    cancellationsTrend: trendOf(cancellations, prevCancellations),
    cancellationRate,
    cancellationRateTrend: trendOf(cancellationRate, prevCancellationRate),
    rating,
    prevRating,
    ratingSeries,
    ratingTrend: ratingCount && prevRatingCount ? trendOf(rating, prevRating) : 'flat',
  };

  return stats;
}

const allAccounts = computed(() => store.getters.userAccounts as UserAccount[]);

const allAccountRows = computed(() => allAccounts.value.map((account) => ({
  account,
  stats: getAccountStats(account.id),
})));

const accountRows = computed(() => {
  const query = (accountNameFilter.value ?? '').trim().toLowerCase();
  if (!query) return allAccountRows.value;
  return allAccountRows.value.filter((row) => (
    row.account.accountName?.toLocaleLowerCase()?.includes(query)
  ));
});

const formatRevenue = (amount: number, currencyCode?: string) => (
  localized.currencyString(amount, currencyCode || 'EUR', true)
);

// revenue totals never sum across currencies: one entry per currency, built
// from the raw per-currency rows so a mixer account's stray currency still counts
const totalsRevenue = computed(() => {
  const perCurrency = new Map<string, CurrencySeries>();
  accountRows.value.forEach((row) => {
    if (row.stats.locked) return;
    const data = accountData.value.get(row.account.id);
    if (data) accumulateCurrencySeries(data.revenues, dayAxis.value, perCurrency);
  });
  const days = selectedDays.value;
  const entries = Array.from(perCurrency.entries()).map(([currencyCode, cs]) => {
    const amount = sum(slicePeriod(cs.series, days, 0));
    const prevAmount = sum(slicePeriod(cs.series, days, 1));
    return {
      currencyCode,
      amount,
      trend: trendOf(amount, prevAmount),
      percent: formatDeltaPercent(amount, prevAmount),
      paymentCount: cs.paymentCount,
    };
  });
  entries.sort((a, b) => b.paymentCount - a.paymentCount || (a.currencyCode < b.currencyCode ? -1 : 1));
  return entries;
});

const totalsRevenueHead = computed(() => {
  const first = totalsRevenue.value[0];
  if (!first) return { text: formatRevenue(0), trend: 'flat' as Trend, percent: '' };
  return { text: formatRevenue(first.amount, first.currencyCode), trend: first.trend, percent: first.percent };
});

const totalsRevenueExtra = computed(() => totalsRevenue.value.slice(1).map((e) => ({
  text: formatRevenue(e.amount, e.currencyCode),
  trend: e.trend,
  percent: e.percent,
})));

const totals = computed(() => {
  const acc = accountRows.value.reduce((total, row) => ({
    guests: total.guests + row.stats.guests,
    prevGuests: total.prevGuests + row.stats.prevGuests,
    reservations: total.reservations + row.stats.reservations,
    prevReservations: total.prevReservations + row.stats.prevReservations,
    noShows: total.noShows + row.stats.noShows,
    prevNoShows: total.prevNoShows + row.stats.prevNoShows,
    cancellations: total.cancellations + row.stats.cancellations,
    prevCancellations: total.prevCancellations + row.stats.prevCancellations,
    ratingSum: total.ratingSum + row.stats.ratingSum,
    prevRatingSum: total.prevRatingSum + row.stats.prevRatingSum,
    ratingCount: total.ratingCount + row.stats.ratingCount,
    prevRatingCount: total.prevRatingCount + row.stats.prevRatingCount,
  }), {
    guests: 0,
    prevGuests: 0,
    reservations: 0,
    prevReservations: 0,
    noShows: 0,
    prevNoShows: 0,
    cancellations: 0,
    prevCancellations: 0,
    ratingSum: 0,
    prevRatingSum: 0,
    ratingCount: 0,
    prevRatingCount: 0,
  });

  const rating = acc.ratingCount ? acc.ratingSum / acc.ratingCount : 0;
  const prevRating = acc.prevRatingCount ? acc.prevRatingSum / acc.prevRatingCount : 0;

  return {
    guests: acc.guests,
    guestsTrend: trendOf(acc.guests, acc.prevGuests),
    guestsPercent: formatDeltaPercent(acc.guests, acc.prevGuests),
    reservations: acc.reservations,
    reservationsTrend: trendOf(acc.reservations, acc.prevReservations),
    reservationsPercent: formatDeltaPercent(acc.reservations, acc.prevReservations),
    noShows: acc.noShows,
    noShowsTrend: trendOf(acc.noShows, acc.prevNoShows),
    noShowsPercent: formatDeltaPercent(acc.noShows, acc.prevNoShows),
    cancellations: acc.cancellations,
    cancellationsTrend: trendOf(acc.cancellations, acc.prevCancellations),
    cancellationsPercent: formatDeltaPercent(acc.cancellations, acc.prevCancellations),
    rating,
    ratingTrend: acc.ratingCount && acc.prevRatingCount ? trendOf(rating, prevRating) : 'flat',
    ratingPercent: acc.ratingCount && acc.prevRatingCount ? formatDeltaPercent(rating, prevRating) : '',
  };
});

const changeAccount = (a: UserAccount) => {
  if (currentAccountId.value === a.id) {
    router.replace('/dashboard');
    return;
  }
  switchAccount(a);
};

// nothing loaded or nothing unlocked -> the summary keeps its layout, no numbers
const totalsShown = computed(() => statsResponse.value !== null && groupHasMultidash.value);
const totalText = (text: string) => (totalsShown.value ? text : '—');
const totalTrend = (trend: Trend): Trend | '' => (totalsShown.value ? trend : '');
const totalPercent = (text: string) => (totalsShown.value ? text : '');

const GRADIENT_UP = ['#00A26F', '#00A26F', '#00A26F', '#4dbe9a', '#FF8262', '#FF002B'];
const GRADIENT_DOWN = ['#FF002B', '#FF8262', '#4dbe9a', '#00A26F'];
const GRADIENT_LOCKED = ['#C6C6C8'];

const gradientFor = (locked: boolean, inverse = false) => {
  if (locked) return GRADIENT_LOCKED;
  return inverse ? GRADIENT_DOWN : GRADIENT_UP;
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;
const formatRating = (rating: number) => (rating ? rating.toFixed(1) : '—');
const trendIcon = (trend: Trend) => (trend === 'up' ? 'mdi-arrow-up-thick' : 'mdi-arrow-down-thick');
const trendColor = (trend: Trend, inverse = false) => {
  if (trend === 'up') return inverse ? 'red' : 'green';
  return inverse ? 'green' : 'red';
};

let refreshTimer: number | undefined;
let lastFetch = 0;

async function fetchStats(background = false) {
  lastFetch = Date.now();
  if (background) {
    // silent refresh - keep showing the last data on failure
    try {
      statsResponse.value = await grpcClient.getMultiAccountStats();
    } catch (e) { /* noop */ }
    return;
  }
  await performAction(null, i18n.tc('error.load_data'), async () => {
    statsResponse.value = await grpcClient.getMultiAccountStats();
  });
}

function onVisibilityChange() {
  if (document.visibilityState === 'visible' && Date.now() - lastFetch > REFRESH_MS) fetchStats(true);
}

let reorderSaving = false;

async function onAccountDrop(evt: Sortable.SortableEvent) {
  const { item, from } = evt;
  const oldIdx = evt.oldDraggableIndex;
  const newIdx = evt.newDraggableIndex;

  // give the moved node back to Vue - the store drives the final order
  from.removeChild(item);
  const rows = from.querySelectorAll(':scope > .account-row');
  if (oldIdx !== undefined && oldIdx < rows.length) from.insertBefore(item, rows[oldIdx]);
  else from.appendChild(item);

  if (oldIdx === undefined || newIdx === undefined || oldIdx === newIdx) return;
  if (accountNameFilter.value || reorderSaving) return;

  // identify the dragged account by identity, not index - the store can
  // re-render mid-drag (update poll) and shift positions under the drag
  const movedId = Number(item.dataset.accountId);
  const prev = allAccounts.value.map((a) => ({ id: a.id, order: a.order }));
  const ids = [...new Set(prev.map((p) => p.id))].filter((id) => id !== movedId);
  if (ids.length === prev.length) return;
  ids.splice(Math.min(newIdx, ids.length), 0, movedId);

  reorderSaving = true;
  try {
    store.dispatch('reorderUserAccounts', { orderedIds: ids });
    const ok = await performSaveAction(null, async () => {
      await grpcClient.setAccountOrder(ids);
    });
    if (!ok) store.dispatch('restoreUserAccountOrders', { orders: prev });
  } finally {
    reorderSaving = false;
  }
}

onMounted(() => {
  if (!hasMultipleAccounts()) {
    router.replace('/dashboard');
    return;
  }

  fetchStats();
  refreshTimer = window.setInterval(() => {
    if (!document.hidden) fetchStats(true);
  }, REFRESH_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);

  const options = {
    handle: '.reorder-handle',
    animation: 150,
    draggable: '.account-row',
    onUpdate: onAccountDrop,
  };
  const elm = document.getElementsByClassName('grid-table-wrapper')[0] as HTMLElement;
  if (elm) Sortable.create(elm, options);
});

onBeforeUnmount(() => {
  window.clearInterval(refreshTimer);
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<style lang="scss">
// five summary tiles without the rating one: split the row evenly instead
// of leaving the sixth slot empty (narrower breakpoints wrap and center
// via the row's justify binding). Vuetify only emits the bare .col class
// when a v-col has no cols prop, so match the sized class.
.multidash-summary--five {
  @media (min-width: 960px) {
    > .col-6 {
      flex: 0 0 20%;
      max-width: 20%;
    }
  }
}

.multidash__root {
  $cols-desktop: 90px minmax(160px, 1.6fr) minmax(90px, 0.8fr) repeat(6, minmax(88px, 1fr));
  $cols-no-city: 90px minmax(160px, 1.6fr) repeat(6, minmax(88px, 1fr));
  $cols-desktop-no-feedback: 90px minmax(160px, 1.6fr) minmax(90px, 0.8fr) repeat(5, minmax(88px, 1fr));
  $cols-no-city-no-feedback: 90px minmax(160px, 1.6fr) repeat(5, minmax(88px, 1fr));
  $compact: 1100px;
  $metric-cols: (E: 1, F: 3, J: 5, G: 7, H: 9, I: 11);

  .grid-table-wrapper,
  .grid-row {
    display: grid;
    grid-template-columns: $cols-desktop;
  }

  &.multidash__root--no-feedback {
    .grid-table-wrapper,
    .grid-row {
      grid-template-columns: $cols-desktop-no-feedback;
    }
  }

  .grid-table-wrapper {
    width: 100%;
    box-sizing: border-box;
  }

  .grid-row {
    grid-column: 1 / -1;

    &:not(.header-row) {
      cursor: pointer;

      > .grid-cell {
        border-top: 1px solid rgba(0, 0, 0, 0.12);

        .theme--dark & {
          border-top: 1px solid rgba(255, 255, 255, 0.12);
        }
      }

      &:hover {
        transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        background-color: $grey-8-color !important;

        .theme--dark & {
          background-color: rgba($grey-66-color, 0.31) !important;
        }
      }
    }

    &.header-row > .grid-cell {
      padding-top: 8px;
      padding-bottom: 8px;
      justify-content: center;
      width: 100%;
    }
  }

  .grid-table-wrapper > .grid-row:last-of-type > .grid-cell {
    border-bottom: none;
  }

  .grid-cell {
    padding: 0 8px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    min-width: 0;
  }

  .multidash__metric {
    align-items: stretch;
    justify-content: center;
  }

  .multidash__account-name-text,
  .multidash__metric-value {
    min-width: 0;
  }

  .multidash__metric-value {
    font-variant-numeric: tabular-nums;
    gap: 2px;
    max-width: 100%;
    overflow: hidden;
  }

  .metric-label {
    display: none;
  }

  .multidash__sparkline {
    height: 32px;
    width: 100%;
    min-width: 0;
    flex: none;
    overflow: hidden;
    line-height: 0;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }

    $spark-heights: (700px: 28px, 560px: 24px, 480px: 16px, 340px: 12px);
    @each $bp, $h in $spark-heights {
      @media (max-width: $bp) {
        height: $h;
      }
    }
  }

  @media #{map-get($display-breakpoints, 'md-and-down')} {
    .multidash__account-name {
      align-items: flex-start;
    }
  }

  @media #{map-get($display-breakpoints, 'md-and-down')} and (min-width: #{$compact + 1}) {
    .grid-table-wrapper,
    .grid-row {
      grid-template-columns: $cols-no-city;
    }

    &.multidash__root--no-feedback {
      .grid-table-wrapper,
      .grid-row {
        grid-template-columns: $cols-no-city-no-feedback;
      }
    }
  }

  @media (max-width: $compact) {
    .header-row {
      display: none;

      + .account-row > .grid-cell {
        border-top: none;
      }
    }

    .metric-label {
      display: block;
      font-size: 10px;
      line-height: 1.2;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: rgba(0, 0, 0, 0.54);
      margin-bottom: 2px;

      .theme--dark & {
        color: rgba(255, 255, 255, 0.54);
      }
    }

    .grid-cell {
      padding: 12px 4px;
    }

    .grid-table-wrapper,
    .grid-row {
      grid-template-columns: repeat(12, 1fr);
      min-width: unset;
    }

    .col-B {
      grid-column: 1 / span 1;
      border: none;
    }

    .col-C {
      grid-column: 2 / span 11;
    }

    @each $name, $start in $metric-cols {
      .col-#{$name} {
        grid-column: $start / span 2;
        border: none;
      }
    }

    @media (max-width: 700px) {
      .col-B {
        grid-column: 1 / span 2;
      }

      .col-C {
        grid-column: 3 / span 10;
      }
    }

    // five metrics without the rating column: 10 tracks keep the row filled
    &.multidash__root--no-feedback {
      .grid-table-wrapper,
      .grid-row {
        grid-template-columns: repeat(10, 1fr);
      }

      .col-C {
        grid-column: 2 / span 9;
      }

      @media (max-width: 700px) {
        .col-C {
          grid-column: 3 / span 8;
        }
      }
    }
  }
}

</style>
